import {
	useCallback, useEffect, useRef, useState,
} from 'react';
import * as OT from '@opentok/client';
import { GetTokboxToken } from '../../connection/opentok';
import { getProfileFromStreamName } from '../../utils/utils';

/** Read on connect rather than at import, so a test can set it. */
const apiKey = () => process.env.REACT_APP_OPENTOK_API_KEY ?? '';

export const MAX_EVENTS = 200;

export const MEASURE_REFRESH_MS = 3000;

/** Above this, a mic is producing something rather than sitting at its floor. */
export const AUDIO_FLOOR = 0.01;

// The layer the room already carries, so the router forwards what it has rather
// than making a publisher encode for this dashboard.
export const MEASURE_RESOLUTION = { width: 320, height: 180 };
export const MEASURE_FRAME_RATE = 7;

// The only source of publisher-side quality, so it is on rather than waiting
// to be asked for. Billed media, and it runs only while the tab is open.
export const MEASURE_BY_DEFAULT = true;

// Marks this connection for every other client, so nothing counts it as one.
export const DASHBOARD_CONNECTION_DATA = { isHealthDashboard: true };

export type SessionEventKind =
	| 'connection-created'
	| 'connection-destroyed'
	| 'stream-created'
	| 'stream-destroyed'
	| 'video-toggled'
	| 'audio-toggled'
	| 'captions-toggled'
	| 'resolution-changed'
	| 'session-reconnecting'
	| 'session-reconnected'
	| 'session-disconnected'
	| 'archive-started'
	| 'archive-stopped'
	| 'video-quality-changed'
	| 'stream-video-disabled'
	| 'stream-video-enabled'
	| 'subscriber-dropped'
	| 'network-condition-changed'
	| 'exception';

export interface SessionEvent {
	at: number;
	/** Identity. Two events can share a millisecond, so the clock is not a key. */
	seq: number;
	kind: SessionEventKind;
	connectionId?: string;
	streamId?: string;
	detail?: string;
	reason?: string;
	code?: number;
	/** Set where the change itself is the degradation - a resolution drop. */
	degraded?: boolean;
}

/**
 * The session as this dashboard's own connection to it sees it - never a fact
 * about a participant.
 */
export interface SessionFacts {
	mediaServer?: string;
	/** Named only when the connection had to relay, so a value here means TURN. */
	relayServer?: string;
	p2p?: boolean;
	messagingServer?: string;
	apiServer?: string;
	partnerId?: string;
	clientVersion?: string;
	/** The signalling socket, which is not the media path. */
	signalingScore?: number;
}

export type ConnectionIdentities = Map<string, number>;

/**
 * Kept after the stream is gone: the live list is pruned on streamDestroyed,
 * and that is exactly when the events worth reading are recorded.
 */
export type StreamIdentities = Map<string, number>;

export interface MonitoredStream {
	streamId: string;
	connectionId: string;
	name: string;
	videoType?: string;
	hasVideo: boolean;
	hasAudio: boolean;
	width?: number;
	height?: number;
	creationTime: number;
}

export interface StreamMeasurement {
	streamId: string;
	name: string;
	/** From `remotePublisherTransport`: the publisher's link, not our downlink. */
	uplink?: string;
	uplinkReason?: string;
	uplinkBandwidth?: number;
	degradationSource?: string;
	/** False is the answer to a blank uplink - that client does not send them. */
	senderStatsAvailable?: boolean;
	/** Measured off the forwarded media, so it is the publisher's own mic. */
	silentForMs?: number;

	codec?: string;
	/** The SDK's own `frameRate` is this same value, deprecated. */
	decodedFrameRate?: number;

	/** Everything below describes the router's link to *this dashboard*. */
	videoKbps?: number;
	audioKbps?: number;
	videoPacketLoss?: number;
	audioPacketLoss?: number;

	freezeCount?: number;
	/** `totalFreezesDuration`, which the SDK reports in milliseconds. */
	freezeMs?: number;
	/** 5s+ with no frame rendered, so a longer break than a freeze. */
	pauseCount?: number;

	/** 1-5, from qualityScoreChanged rather than getStats. */
	audioMos?: number;
	videoMos?: number;

	error?: string;
}

interface StatsSample {
	at: number;
	videoBytes: number;
	audioBytes: number;
	videoPacketsLost: number;
	videoPackets: number;
	audioPacketsLost: number;
	audioPackets: number;
}

const rate = (current: number, previous: number, seconds: number): number | undefined => (
	seconds > 0 ? Math.max(0, Math.round(((current - previous) * 8) / seconds / 1000)) : undefined
);

const lossRatio = (
	lost: number,
	received: number,
	previousLost: number,
	previousReceived: number,
): number | undefined => {
	const periodLost = lost - previousLost;
	const periodReceived = received - previousReceived;
	const expected = periodLost + periodReceived;
	return expected > 0 ? periodLost / expected : undefined;
};

/**
 * Greenroom sets no connection data for an ordinary participant, so this only
 * ever describes a SIP caller or an onsite client.
 */
export const describeConnection = (data?: string): { label?: string; name?: string } => {
	if (!data) return {};

	try {
		const parsed = JSON.parse(data);
		if (parsed?.sip) return { label: 'sip', name: parsed.name };
		if (parsed?.onsiteLocationId) return { label: `onsite ${parsed.onsiteLocationId}` };
		return {};
	} catch {
		return {};
	}
};

export const isDashboardConnection = (data?: string): boolean => {
	if (!data) return false;
	try {
		return !!JSON.parse(data)?.isHealthDashboard;
	} catch {
		return data.includes('isHealthDashboard');
	}
};

export interface UseSessionMonitorResult {
	/** Newest first. */
	events: SessionEvent[];
	streams: MonitoredStream[];
	connectionCount: number;
	connected: boolean;
	error?: string;
	/** What the token granted. A read-only view must not be able to publish. */
	capabilities?: OT.Session['capabilities'];
	/** The marker did not land, so other clients cannot tell this from a peer. */
	unmarked: boolean;
	subscribed: boolean;
	setSubscribed: (value: boolean) => void;
	measuring: boolean;
	measurements: StreamMeasurement[];
	measuredAt: number | null;
	/** connectionId to the gr_user publishing on it. */
	identities: ConnectionIdentities;
	streamIdentities: StreamIdentities;
	/** Only SIP callers have one, and no roster will ever name them. */
	connectionNames: Map<string, string>;
	/** Empty until measuring is on and a subscription has reported. */
	sessionFacts: SessionFacts;
}

const describeTokenFailure = (response: unknown): string => {
	if (typeof response === 'number') return `http ${response}`;
	if (response instanceof Error) return response.message;
	return 'no token in the response';
};

/**
 * A camera is `<gr_user>,<guid>`, a mobile one `mob-<id>`, and a browser-picker
 * screenshare the bare gr_user - so a parser insisting on the comma missed two.
 */
const grUserFromName = (name?: string): number | undefined => {
	if (!name) return undefined;
	const grUser = getProfileFromStreamName(name);
	return Number.isFinite(grUser) && grUser > 0 ? grUser : undefined;
};

const toStream = (stream: OT.Stream): MonitoredStream => ({
	streamId: stream.streamId,
	connectionId: stream.connection?.connectionId,
	name: stream.name,
	videoType: stream.videoType,
	hasVideo: stream.hasVideo,
	hasAudio: stream.hasAudio,
	width: stream.videoDimensions?.width,
	height: stream.videoDimensions?.height,
	creationTime: stream.creationTime,
});

const dimensions = (value: any): string => (
	value?.width && value?.height ? `${value.width}x${value.height}` : 'unknown'
);

/** Rotating a device or switching camera changes this without degrading it. */
const pixels = (value: any): number => (value?.width ?? 0) * (value?.height ?? 0);

/**
 * Connects to the room's OpenTok session, records the server's own event stream,
 * and subscribes to each publisher to measure it. A stats poll shows a
 * participant at two instants; this shows the transition between them.
 */
export default function useSessionMonitor(
	sessionId: string | null,
	/** False disconnects and releases everything. Hold it on across tab changes. */
	enabled: boolean,
): UseSessionMonitorResult {
	const [events, setEvents] = useState<SessionEvent[]>([]);
	const [streams, setStreams] = useState<Map<string, MonitoredStream>>(new Map());
	const [connections, setConnections] = useState<Set<string>>(new Set());
	const [connected, setConnected] = useState(false);
	const [error, setError] = useState<string | undefined>();
	const [capabilities, setCapabilities] = useState<OT.Session['capabilities']>();
	const sessionRef = useRef<OT.Session | null>(null);
	const [subscribed, setSubscribed] = useState(MEASURE_BY_DEFAULT);
	const [measuring, setMeasuring] = useState(false);
	const [measurements, setMeasurements] = useState<StreamMeasurement[]>([]);
	const [measuredAt, setMeasuredAt] = useState<number | null>(null);
	const [identities, setIdentities] = useState<ConnectionIdentities>(new Map());
	const [streamIdentities, setStreamIdentities] = useState<StreamIdentities>(new Map());
	const [connectionNames, setConnectionNames] = useState<Map<string, string>>(new Map());
	const [sessionFacts, setSessionFacts] = useState<SessionFacts>({});
	const qos = useRef<Map<string, { senderStatsAvailable?: boolean }>>(new Map());
	const [unmarked, setUnmarked] = useState(false);
	const samples = useRef<Map<string, StatsSample>>(new Map());
	// In refs rather than the effect: it reconciles one stream at a time, so none
	// of this can be rebuilt per run.
	const subscribers = useRef<Map<string, {
		subscriber: OT.Subscriber;
		/** Outlives `sessionRef` being nulled, so it can still unsubscribe. */
		session: OT.Session;
	}>>(new Map());
	const subscribedAt = useRef<Map<string, number>>(new Map());
	const heard = useRef<Map<string, number>>(new Map());
	const scores = useRef<Map<string, {
		audioQualityScore?: number;
		videoQualityScore?: number;
	}>>(new Map());
	/** Lets a new stream be sampled without restarting everyone else's interval. */
	const readRef = useRef<(() => void) | undefined>(undefined);
	/** Which streams exist, as a value - the Map's identity changes constantly. */
	const streamKey = [...streams.keys()].sort().join(',');
	/** Read by the poll, which must not restart when a name or a size changes. */
	const streamsRef = useRef(streams);
	streamsRef.current = streams;
	const nextSeq = useRef(0);
	const selfConnection = useRef<string | undefined>(undefined);

	/**
	 * `at` is passed in for the state OpenTok replays on connect - it began before
	 * this dashboard did, and stamping it with now dated the whole room to the
	 * moment the tab opened. That replay also arrives out of order, hence the
	 * sort, with `seq` breaking ties within a millisecond.
	 */
	const record = useCallback((event: Omit<SessionEvent, 'at' | 'seq'>, at?: number) => {
		nextSeq.current += 1;
		const entry: SessionEvent = { at: at || Date.now(), seq: nextSeq.current, ...event };
		setEvents((previous) => [entry, ...previous]
			.sort((a, b) => b.at - a.at || b.seq - a.seq)
			.slice(0, MAX_EVENTS));
	}, []);

	useEffect(() => {
		if (!enabled || !sessionId) return undefined;

		const key = apiKey();
		if (!key) {
			setError('REACT_APP_OPENTOK_API_KEY is not set');
			return undefined;
		}

		let cancelled = false;

		const session = OT.initSession(key, sessionId);
		sessionRef.current = session;

		// Named so the cleanup can take them off: initSession hands back the *same*
		// Session for a given id and disconnect() leaves handlers attached, so a
		// second run would stack another set and record the replay twice.

		// Two checks because neither covers everything - the marker catches other
		// dashboards but only if it landed, our own id catches this one either way.
		const isOwnKind = (connection: any): boolean => (
			isDashboardConnection(connection?.data)
			|| (!!selfConnection.current && connection?.connectionId === selfConnection.current)
		);

		const handlers: Record<string, (event: any) => void> = {
			connectionCreated: (event) => {
				if (isOwnKind(event.connection)) return;
				const { connectionId } = event.connection;
				setConnections((previous) => new Set(previous).add(connectionId));

				// A SIP caller is in no roster, so the name on the connection is the
				// only one there will ever be for it.
				const { label, name } = describeConnection(event.connection?.data);
				if (name) {
					setConnectionNames((previous) => new Map(previous).set(connectionId, name));
				}

				record(
					{ kind: 'connection-created', connectionId, detail: label },
					event.connection.creationTime,
				);
			},

			connectionDestroyed: (event) => {
				if (isOwnKind(event.connection)) return;
				setConnections((previous) => {
					const next = new Set(previous);
					next.delete(event.connection.connectionId);
					return next;
				});
				record({
					kind: 'connection-destroyed',
					connectionId: event.connection.connectionId,
					reason: event.reason,
				});
			},

			streamCreated: (event) => {
				// The stream name is the only place a connection is tied to a person.
				const grUser = grUserFromName(event.stream.name);
				const connectionId = event.stream.connection?.connectionId;
				if (grUser && connectionId) {
					setIdentities((previous) => new Map(previous).set(connectionId, grUser));
				}
				if (grUser) {
					setStreamIdentities((previous) => (
						new Map(previous).set(event.stream.streamId, grUser)
					));
				}

				const upsert = (previous: Map<string, MonitoredStream>) => (
					new Map(previous).set(event.stream.streamId, toStream(event.stream))
				);
				setStreams(upsert);
				record({
					kind: 'stream-created',
					streamId: event.stream.streamId,
					connectionId: event.stream.connection?.connectionId,
					detail: event.stream.videoType,
				}, event.stream.creationTime);
			},

			streamDestroyed: (event) => {
				setStreams((previous) => {
					const next = new Map(previous);
					next.delete(event.stream.streamId);
					return next;
				});
				record({
					kind: 'stream-destroyed',
					streamId: event.stream.streamId,
					reason: event.reason,
				});
			},

			streamPropertyChanged: (event) => {
				const upsert = (previous: Map<string, MonitoredStream>) => (
					new Map(previous).set(event.stream.streamId, toStream(event.stream))
				);
				setStreams(upsert);

				if (event.changedProperty === 'videoDimensions') {
					record({
						kind: 'resolution-changed',
						streamId: event.stream.streamId,
						detail: `${dimensions(event.oldValue)} → ${dimensions(event.newValue)}`,
						degraded: pixels(event.newValue) < pixels(event.oldValue),
					});
					return;
				}

				const TOGGLES: Record<string, SessionEventKind> = {
					hasVideo: 'video-toggled',
					hasAudio: 'audio-toggled',
					hasCaptions: 'captions-toggled',
				};
				const toggled = TOGGLES[event.changedProperty];
				if (toggled) {
					record({
						kind: toggled,
						streamId: event.stream.streamId,
						detail: event.newValue ? 'on' : 'off',
					});
				}
			},

			sessionReconnecting: () => record({ kind: 'session-reconnecting' }),
			sessionReconnected: () => record({ kind: 'session-reconnected' }),
			sessionDisconnected: (event) => {
				setConnected(false);
				record({ kind: 'session-disconnected', reason: event?.reason });
			},
			// The id is what EndRecording and sync's get-archive both key on.
			archiveStarted: (event) => record({
				kind: 'archive-started',
				detail: [event?.name, event?.id].filter(Boolean).join(' · '),
			}),
			archiveStopped: (event) => record({
				kind: 'archive-stopped',
				detail: [event?.name, event?.id].filter(Boolean).join(' · '),
			}),
		};

		// Before connect: OpenTok replays the session's current state on connecting.
		Object.entries(handlers).forEach(([name, handler]) => session.on(name, handler));

		// The marker is a nice-to-have; connecting is not. An unrecognised `data`
		// payload is the first thing to suspect when the token comes back empty.
		const fetchToken = async (): Promise<any> => {
			const marked = await GetTokboxToken(sessionId, DASHBOARD_CONNECTION_DATA);
			if (marked?.token) return marked;

			const plain = await GetTokboxToken(sessionId);
			if (plain?.token) {
				setUnmarked(true);
				return plain;
			}

			return marked ?? plain;
		};

		fetchToken().then((response: any) => {
			if (cancelled) return;

			if (!response?.token) {
				setError(`could not get a session token (${describeTokenFailure(response)})`);
				return;
			}

			session.connect(response.token, (connectError?: OT.OTError) => {
				if (cancelled) return;

				if (connectError) {
					setError(connectError.message ?? 'could not connect to the session');
					return;
				}

				setError(undefined);
				setConnected(true);
				setCapabilities(session.capabilities);

				// The replay can land before this callback, when there was no
				// `session.connection` yet to recognise ourselves by.
				const selfId = session.connection?.connectionId;
				selfConnection.current = selfId;
				if (selfId) {
					setConnections((previous) => {
						if (!previous.has(selfId)) return previous;
						const next = new Set(previous);
						next.delete(selfId);
						return next;
					});
					setEvents((previous) => previous.filter((entry) => entry.connectionId !== selfId));
				}
			});
		});

		return () => {
			cancelled = true;
			// The session outlives this effect, so its handlers have to come off.
			Object.entries(handlers).forEach(([name, handler]) => session.off(name, handler));
			session.disconnect();
			sessionRef.current = null;
			setConnected(false);
		};
	}, [sessionId, enabled, record]);

	/**
	 * The SDK's own definition of a fault, rather than a guess from a reason
	 * string. `OT.on` is global, so the target's session is checked - this tab
	 * runs inside the Greenroom app, and a producer sitting in the room would
	 * otherwise see their own publisher's errors here.
	 */
	useEffect(() => {
		if (!enabled || !sessionId) return undefined;

		const onException = (event: any) => {
			const target = event?.target;
			if ((target?.sessionId ?? target?.session?.sessionId) !== sessionId) return;

			record({
				kind: 'exception',
				code: event?.code,
				detail: [event?.title, event?.message].filter(Boolean).join(' · '),
			});
		};

		OT.on('exception', onException);
		return () => OT.off('exception', onException);
	}, [sessionId, enabled, record]);

	/**
	 * One subscription per publishing stream, reconciled rather than rebuilt and
	 * held for as long as the monitor is enabled. Dropping and remaking one is
	 * churn on every publisher in the room, so a tab change is not worth it.
	 */
	useEffect(() => {
		const session = sessionRef.current;
		const wanted = subscribed && connected && !!session;

		const forget = (streamId: string) => {
			subscribers.current.delete(streamId);
			subscribedAt.current.delete(streamId);
			heard.current.delete(streamId);
			scores.current.delete(streamId);
			qos.current.delete(streamId);
			// Counters restart with the next subscription, so a kept sample would
			// read as a huge negative rate.
			samples.current.delete(streamId);
		};

		// Through the session that created it: by the time the monitor is disabled,
		// `sessionRef` has already been cleared by the effect above.
		let released = false;
		subscribers.current.forEach((entry, streamId) => {
			if (wanted && streams.has(streamId)) return;
			entry.session.unsubscribe(entry.subscriber);
			forget(streamId);
			released = true;
		});

		// A publisher who stops should leave the table now, not on the next read.
		if (released) {
			setMeasurements((previous) => previous.filter(
				(measurement) => subscribers.current.has(measurement.streamId),
			));
		}

		if (!wanted) {
			setMeasuring(false);
			setMeasurements([]);
			return undefined;
		}

		let added = false;

		streams.forEach((entry, streamId) => {
			if (subscribers.current.has(streamId)) return;

			const stream = (session as any).streams?.find?.(
				(candidate: OT.Stream) => candidate.streamId === streamId,
			);
			if (!stream) return;

			// Video, because without a track there is no MOS, freeze count or loss
			// figure - but pinned low so no publisher encodes for this dashboard.
			const subscriber = session!.subscribe(stream, undefined, {
				subscribeToVideo: true,
				preferredResolution: MEASURE_RESOLUTION,
				preferredFrameRate: MEASURE_FRAME_RATE,
				insertDefaultUI: false,
				audioVolume: 0,
			});

			subscribers.current.set(streamId, { subscriber, session: session! });
			subscribedAt.current.set(streamId, Date.now());
			added = true;

			// Fires up to 60x a second, so latched rather than pushed into state. What is
			// kept is the last moment there was sound: an instantaneous level drops
			// between syllables and would call someone mid-sentence dead.
			subscriber.on?.('audioLevelUpdated', (event: any) => {
				if (event.audioLevel > AUDIO_FLOOR) heard.current.set(streamId, Date.now());
			});

			// MOS is event-only; getStats never reports it. Latched for the next
			// read rather than patched in, which would rebuild every measurement.
			subscriber.on?.('qualityScoreChanged', (event: any) => {
				scores.current.set(streamId, event?.qualityScore ?? {});
			});

			/*
				The body of the ClientQos report the SDK posts to Vonage, handed over
				locally. Undocumented and in no typing, so it is read defensively and
				nothing depends on it arriving. Only the fields describing the
				publisher or the session are kept - the rest is this machine's own
				receive path and would read as a fact about the participant.
			*/
			subscriber.on?.('qos', (event: any) => {
				qos.current.set(streamId, {
					senderStatsAvailable: event?.subscribeToSenderStats,
				});
				// Every subscriber reports the same fields, so most of these carry
				// nothing new. Returning `previous` is what stops the re-render.
				setSessionFacts((previous) => {
					const next: SessionFacts = {
						...previous,
						mediaServer: event?.mediaServerName ?? previous.mediaServer,
						relayServer: event?.relayServer ?? previous.relayServer,
						p2p: event?.p2p ?? previous.p2p,
						messagingServer: event?.messagingServer ?? previous.messagingServer,
						apiServer: event?.apiServer ?? previous.apiServer,
						partnerId: event?.partnerId ?? previous.partnerId,
						clientVersion: event?.clientVersion ?? previous.clientVersion,
						signalingScore: event?.signalingNetworkQualityScore ?? previous.signalingScore,
					};
					const changed = (Object.keys(next) as (keyof SessionFacts)[])
						.some((field) => next[field] !== previous[field]);
					return changed ? next : previous;
				});
			});

			// The moment the publisher's link moved, which the poll below can only
			// catch on its next read. Its own half of the report is this machine's.
			subscriber.on?.('networkConditionChanged', (event: any) => {
				const remote = event?.stats?.mediaLink?.remotePublisherTransport;
				const condition = remote?.networkCondition;
				if (!condition || condition === 'unknown') return;

				const reason = remote?.networkConditionReason;
				const source = event?.stats?.mediaLink?.networkDegradationSource;
				record({
					kind: 'network-condition-changed',
					streamId,
					// Not `reason` - the event's own is always its own name.
					detail: [
						`publisher ${condition}`,
						reason && reason !== 'none' ? reason : undefined,
						source && source !== 'none' ? `source ${source}` : undefined,
					].filter(Boolean).join(' · '),
					degraded: condition === 'critical' || condition === 'warning',
				});
			});

			// A later stats sample shows the effect but never the cause.
			subscriber.on?.('videoQualityChanged', (event: any) => record({
				kind: 'video-quality-changed',
				streamId,
				reason: event?.reason,
			}));

			subscriber.on?.('videoDisabled', (event: any) => record({
				kind: 'stream-video-disabled',
				streamId,
				reason: event?.reason,
			}));

			subscriber.on?.('videoEnabled', (event: any) => record({
				kind: 'stream-video-enabled',
				streamId,
				reason: event?.reason,
			}));

			// A drop that recovers leaves no trace in the next stats sample.
			subscriber.on?.('disconnected', () => record({
				kind: 'subscriber-dropped',
				streamId,
			}));
		});

		// A new stream should not wait out the rest of someone else's interval.
		if (added) readRef.current?.();

		// No cleanup: the run above releases whatever is no longer wanted, and
		// unmounting disconnects the session, which drops the rest.
		return undefined;
		// `streamKey` rather than `streams`: every streamPropertyChanged builds a
		// new Map, and depending on its identity re-ran all of this on a mute.
	}, [subscribed, connected, streamKey, record]);

	/** One poll for every subscription, so a new publisher restarts nobody. */
	useEffect(() => {
		if (!subscribed || !connected) return undefined;

		let cancelled = false;

		const readOne = (subscriber: OT.Subscriber) => new Promise<any>((resolve) => {
			subscriber.getStats((statsError?: OT.OTError, stats?: any) => (
				resolve(statsError ? null : stats)
			));
		});

		const read = async () => {
			const entries = [...subscribers.current.entries()];
			if (!entries.length) return;

			setMeasuring(true);
			const all = await Promise.all(entries.map(([, entry]) => readOne(entry.subscriber)));
			if (cancelled) return;

			const now = Date.now();

			setMeasurements(entries.map(([streamId], index) => {
				const stats = all[index];
				const remote = stats?.mediaLink?.remotePublisherTransport;
				const video = stats?.video;
				const audio = stats?.audio;
				const score = scores.current.get(streamId);

				// getStats hands back totals since subscribing, so the previous
				// sample is what turns them into a rate.
				const previous = samples.current.get(streamId);
				const seconds = previous ? (now - previous.at) / 1000 : 0;

				if (stats) {
					samples.current.set(streamId, {
						at: now,
						videoBytes: video?.bytesReceived ?? 0,
						audioBytes: audio?.bytesReceived ?? 0,
						videoPacketsLost: video?.packetsLost ?? 0,
						videoPackets: video?.packetsReceived ?? 0,
						audioPacketsLost: audio?.packetsLost ?? 0,
						audioPackets: audio?.packetsReceived ?? 0,
					});
				}

				return {
					streamId,
					name: streamsRef.current.get(streamId)?.name ?? '',
					uplink: remote?.networkCondition,
					uplinkReason: remote?.networkConditionReason,
					uplinkBandwidth: remote?.connectionEstimatedBandwidth,
					degradationSource: stats?.mediaLink?.networkDegradationSource,
					senderStatsAvailable: qos.current.get(streamId)?.senderStatsAvailable,
					silentForMs: now - (
						heard.current.get(streamId) ?? subscribedAt.current.get(streamId) ?? now
					),
					codec: video?.codec,
					decodedFrameRate: video?.decodedFrameRate,
					videoKbps: previous && video
						? rate(video.bytesReceived, previous.videoBytes, seconds)
						: undefined,
					audioKbps: previous && audio
						? rate(audio.bytesReceived, previous.audioBytes, seconds)
						: undefined,
					videoPacketLoss: previous && video
						? lossRatio(
							video.packetsLost,
							video.packetsReceived,
							previous.videoPacketsLost,
							previous.videoPackets,
						)
						: undefined,
					audioPacketLoss: previous && audio
						? lossRatio(
							audio.packetsLost,
							audio.packetsReceived,
							previous.audioPacketsLost,
							previous.audioPackets,
						)
						: undefined,
					freezeCount: video?.freezeCount,
					freezeMs: video?.totalFreezesDuration,
					pauseCount: video?.pauseCount,
					audioMos: score?.audioQualityScore,
					videoMos: score?.videoQualityScore,
					error: stats ? undefined : 'no stats',
				};
			}));
			setMeasuredAt(Date.now());
			setMeasuring(false);
		};

		readRef.current = read;
		read();
		const timer = setInterval(read, MEASURE_REFRESH_MS);

		return () => {
			cancelled = true;
			readRef.current = undefined;
			clearInterval(timer);
			setMeasuring(false);
		};
	}, [subscribed, connected]);
	return {
		events,
		streams: [...streams.values()],
		connectionCount: connections.size,
		connected,
		error,
		capabilities,
		unmarked,
		subscribed,
		setSubscribed,
		measuring,
		measurements,
		measuredAt,
		identities,
		streamIdentities,
		connectionNames,
		sessionFacts,
	};
}
